import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import { Message } from 'semantic-ui-react';

import Search from '../views/Shared/Search';
import withPagination from './WithPagination';
import LoadingDataEnhancer from './LoadingData';
import GuaranteePurchasesList from '../views/CancelledGuaranteePurchases/List';
import GuaranteeReinstate from '../views/CancelledGuaranteePurchases/ReinstateGuaranteeForm';
import cancelledPurchasesQuery from '../graphql/cancelledPurchasesQuery';
import { setSearch } from '../data/searchField/actions';
import { setPage } from '../data/page/actions';
import reinstateGuaranteePurchaseMutation from '../graphql/reinstateGuaranteePurchaseMutation';
import CancelledGuaranteePurchasesAudit from '../views/CancelledGuaranteePurchases/Audit';

const CancelledGuaranteePurchases = props => {
  const {
    data: { cancelledPurchases, refetch },
    searchFields,
    setSearchFields: setSearchFieldsAction,
    setPage: setPageAction,
    reinstateMutation,
  } = props;
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [selectedGuaranteePurchase, setSelectedGuaranteePurchase] = useState(null);
  const [isReinstateModalOpen, setIsReinstateModalOpen] = useState(false);
  const [reinstateFormError, setReinstateFormError] = useState(null);
  const [reinstateFormSubmitting, setReinstateFormSubmitting] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);

  const handleMessageDismiss = useCallback(() => {
    setFlashMessage(null);
  }, []);

  const onSelectGuarantee = useCallback((action, guarantee) => {
    setSelectedGuaranteePurchase(guarantee);

    switch (action) {
      case 'Reinstate Guarantee':
        setIsReinstateModalOpen(true);
        setReinstateFormError(null);
        setReinstateFormSubmitting(false);
        break;
      case 'Status History':
        setIsAuditModalOpen(true);
        break;
      default:
        setSelectedGuaranteePurchase(null);
    }
  }, []);

  const onSearchSubmitHandler = useCallback(event => {
    const { vin } = event.target;
    const variableArguments = {
      vin: vin.value.trim() || null,
    };
    refetch({
      ...variableArguments,
    });

    setSearchFieldsAction(variableArguments);
    setPageAction({});
  }, [refetch, setSearchFieldsAction, setPageAction]);

  const onAuditCloseHandler = useCallback(() => {
    setIsAuditModalOpen(false);
    setSelectedGuaranteePurchase(null);
  }, []);

  const onReinstateFormCloseHandler = useCallback(() => {
    setIsReinstateModalOpen(false);
    setSelectedGuaranteePurchase(null);
  }, []);

  const onReinstateFormSubmitHandler = useCallback(values => {
    setReinstateFormSubmitting(true);

    const reinstateInput = { ...values };
    reinstateMutation({
      variables: { reinstateInput },
    })
      .then(({ data }) => {
        const {
          reinstateGuaranteePurchase: { success, errors },
        } = data;
        if (success) {
          refetch();
          setIsReinstateModalOpen(false);
          setSelectedGuaranteePurchase(null);
          setReinstateFormSubmitting(false);
          setFlashMessage('Successfully Reinstate Guarantee Purchase');
        } else {
          setReinstateFormSubmitting(false);
          setReinstateFormError(errors);
        }
      })
      .catch(() => {
        setReinstateFormSubmitting(false);
        setReinstateFormError('Error while reinstate Guarantee Purchase');
      });
  }, [reinstateMutation, refetch]);

  return (
    <div>
      {flashMessage && (
        <Message
          onDismiss={handleMessageDismiss}
          content={flashMessage}
          positive
        />
      )}
      <Search
        onSubmit={onSearchSubmitHandler}
        searchFields={searchFields}
        searchOptions={['vin']}
      />
      <GuaranteePurchasesList
        onSelectGuarantee={onSelectGuarantee}
        cancelledPurchases={cancelledPurchases}
      />
      { isAuditModalOpen && selectedGuaranteePurchase && (
        <CancelledGuaranteePurchasesAudit
          isAuditModalOpen={isAuditModalOpen}
          onCloseHandler={onAuditCloseHandler}
          cancelledGuaranteePurchase={selectedGuaranteePurchase}
        />
      )}
      {selectedGuaranteePurchase && isReinstateModalOpen && (
        <div>
          <GuaranteeReinstate
            isReinstateModalOpen={isReinstateModalOpen}
            onCloseHandler={onReinstateFormCloseHandler}
            onSubmitHandler={onReinstateFormSubmitHandler}
            cancelledPurchase={selectedGuaranteePurchase}
            error={reinstateFormError}
            submitting={reinstateFormSubmitting}
          />
        </div>
      )}
    </div>
  );
};

CancelledGuaranteePurchases.propTypes = {
  data: PropTypes.shape({
    cancelledPurchases: PropTypes.array,
    refetch: PropTypes.func,
  }).isRequired,
  searchFields: PropTypes.object.isRequired,
  setSearchFields: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  reinstateMutation: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
  setPage: () => {
    dispatch(setPage());
  },
});

const mapStateToProps = state => {
  const {
    data: { searchFields },
  } = state;
  return {
    searchFields,
  };
};

export default compose(
  withRouter,
  withPagination(cancelledPurchasesQuery, 'cancelledPurchases'),
  graphql(reinstateGuaranteePurchaseMutation, { name: 'reinstateMutation' }),
  LoadingDataEnhancer,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(CancelledGuaranteePurchases);
